import React from 'react';
import type { ProductionRequest, ProductionRequestLine } from '@/services/productionRequest.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import './ProductionApprovalFormPrint.css';

export type ProductionApprovalFormPrintProps = {
  pr: ProductionRequest;
  companyLegalName: string;
  companyLogoUrl?: string;
  documentFormNo?: string;
};

function formatDdMmYyyy(iso: string | null | undefined): string {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (d && m && y) return `${d}/${m}/${y}`;
  return s;
}

function formatUser(u?: { first_name?: string; last_name?: string; username?: string } | null): string {
  if (!u) return '';
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return name || u.username || '';
}

function waiverReasonLabel(pr: ProductionRequest): string {
  const r = pr.po_waiver_reason;
  if (!r) return '';
  if (r === 'PO_will_follow_before_delivery') return 'PO will follow before delivery';
  if (r === 'Other') return pr.po_waiver_reason_detail ? `Other — ${pr.po_waiver_reason_detail}` : 'Other';
  return String(r);
}

function lineDescription(line: ProductionRequestLine): string {
  const parts: string[] = [];
  if (line.item?.item_code || line.item?.item_name) {
    const inv = [line.item?.item_code, line.item?.item_name].filter(Boolean).join(' — ');
    if (inv) parts.push(inv);
  }
  if (line.manual_product_code?.trim()) parts.push(line.manual_product_code.trim());
  if (line.manual_product_description?.trim()) parts.push(line.manual_product_description.trim());
  if (line.remarks?.trim()) parts.push(line.remarks.trim());
  return parts.length ? parts.join('\n') : '—';
}

function formatBoqMaterialRow(m: {
  description?: string | null;
  heat_number?: string | null;
  mtc_document_number?: string | null;
}): string {
  const parts: string[] = [];
  const desc = (m.description ?? '').trim();
  if (desc) parts.push(desc);
  const heat = (m.heat_number ?? '').trim();
  if (heat) parts.push(`Heat: ${heat}`);
  const mtc = (m.mtc_document_number ?? '').trim();
  if (mtc) parts.push(`MTC: ${mtc}`);
  return parts.join(' · ');
}

function lineMaterial(line: ProductionRequestLine): string {
  const fromBoq = (line.boq_materials ?? [])
    .map((m) => formatBoqMaterialRow(m))
    .filter(Boolean);
  if (fromBoq.length) return fromBoq.join('; ');
  return line.bom?.bom_number || line.bom?.bom_name || '—';
}

/** Production approval form — shared pagination engine with restored scope/sign-off. */
export const ProductionApprovalFormPrint: React.FC<ProductionApprovalFormPrintProps> = ({
  pr,
  companyLegalName,
  documentFormNo,
}) => {
  const refNo = (pr.external_ref_no ?? '').trim() || pr.request_number;
  const customerName = pr.customer?.customer_name || pr.customer?.customer_code || '';
  const lines = pr.lines ?? [];

  return (
    <EngineCommercialA4Print
      docType="production_approval"
      title="APPROVAL FORM"
      documentNumber={pr.request_number || 'PA'}
      partyName={customerName}
      introText={companyLegalName}
      metaLines={[
        { label: 'Ref No', value: refNo || '' },
        { label: 'Date', value: formatDdMmYyyy(pr.request_date) },
        { label: 'Effective Date', value: formatDdMmYyyy(pr.effective_date) },
        { label: 'Company Name', value: customerName },
        { label: 'Document No', value: documentFormNo?.trim() || '' },
        { label: 'Job No', value: pr.request_number || '' },
        { label: 'Estimation No', value: pr.quotation?.quotation_number || '' },
        { label: 'Contact Person', value: (pr.contact_person ?? '').trim() },
        { label: 'Reason for Approval', value: waiverReasonLabel(pr) },
        { label: 'Notes', value: (pr.responsibility_notes ?? '').trim() },
      ].filter((m) => m.value)}
      columns={[
        { key: 'sno', label: 'Sr No' },
        { key: 'description', label: 'Description' },
        { key: 'qty', label: 'Quantity' },
        { key: 'material', label: 'Material' },
      ]}
      lines={lines.map((line) => ({
        lineNumber: line.line_number,
        description: lineDescription(line),
        cells: {
          qty: `${Number(line.quantity).toLocaleString(undefined, { maximumFractionDigits: 4 })} ${line.uom || ''}`.trim(),
          material: lineMaterial(line),
        },
      }))}
      finals={[{ id: 'sign', heightPx: 110 }]}
      finalBlocks={{
        sign: (
          <div className="pa-signatures">
            <div className="pa-sig-block">
              <div className="pa-sig-role">Estimation</div>
              <div className="pa-sig-line" />
              <div className="pa-sig-name">{formatUser(pr.signoffEstimationUser) || '\u00a0'}</div>
            </div>
            <div className="pa-sig-block">
              <div className="pa-sig-role">Operation Manager</div>
              <div className="pa-sig-line" />
              <div className="pa-sig-name">{formatUser(pr.signoffOperationsUser) || '\u00a0'}</div>
            </div>
          </div>
        ),
      }}
    />
  );
};
